import {
	DEFAULT_VIDEO_CONSTRAINTS,
	SELECTED_AUDIO_INPUT_KEY,
	SELECTED_VIDEO_INPUT_KEY,
} from '../../../constants';
import { useCallback, useEffect, useRef, useState } from 'react';
import * as Video from 'twilio-video';
import {
	LocalAudioTrack,
	LocalVideoTrack,
	CreateLocalTrackOptions,
	CreateLocalAudioTrackOptions,
} from 'twilio-video';
import { useAppState } from '../../../state';

type Kind = 'audio' | 'video';
export default function useLocalTracks() {
	const { setIsKrispEnabled, setIsKrispInstalled } = useAppState();
	const tracks = useRef<{ audio?: LocalAudioTrack; video?: LocalVideoTrack }>(
		{},
	);
	const generation = useRef({ audio: 0, video: 0 });
	const pending = useRef<Partial<Record<Kind, Promise<any>>>>({});
	const alive = useRef(true);
	const [, render] = useState(0);
	const [isAcquiringLocalTracks, setBusy] = useState(false);
	const audioEnabled = useRef(true);
	const videoWanted = useRef(true);
	const refresh = useCallback(() => {
		if (alive.current) render((value) => value + 1);
	}, []);
	const remove = useCallback(
		(kind: Kind, preserve = true) => {
			generation.current[kind]++;
			const track = tracks.current[kind];
			if (kind === 'audio' && track)
				audioEnabled.current = track.isEnabled;
			if (kind === 'video' && !preserve) videoWanted.current = false;
			track?.off('started', refresh);
			track?.off('stopped', refresh);
			track?.stop();
			delete tracks.current[kind];
			refresh();
		},
		[refresh],
	);
	useEffect(() => {
		alive.current = true;
		return () => {
			alive.current = false;
			remove('audio');
			remove('video');
		};
	}, [remove]);

	const acquire = useCallback(
		(kind: Kind, overrides?: CreateLocalTrackOptions): Promise<any> => {
			if (pending.current[kind]) return pending.current[kind]!;
			if (tracks.current[kind] && !tracks.current[kind]!.isStopped)
				return Promise.resolve(tracks.current[kind]);
			const attempt = generation.current[kind];
			setBusy(true);
			const key =
				kind === 'audio'
					? SELECTED_AUDIO_INPUT_KEY
					: SELECTED_VIDEO_INPUT_KEY;
			const selected = localStorage.getItem(key);
			const constraints: CreateLocalAudioTrackOptions = {
				...(kind === 'video'
					? {
							...(DEFAULT_VIDEO_CONSTRAINTS as object),
							name: `camera-${Date.now()}`,
						}
					: {
							noiseCancellationOptions: {
								sdkAssetsPath: '/noisecancellation',
								vendor: 'krisp',
							},
						}),
				...(selected ? { deviceId: { exact: selected } } : {}),
				...overrides,
			};
			const create = (options: CreateLocalTrackOptions) =>
				kind === 'audio'
					? Video.createLocalAudioTrack(options)
					: Video.createLocalVideoTrack(options);
			const operation = (async () => {
				let track: LocalAudioTrack | LocalVideoTrack;
				try {
					track = await create(constraints);
				} catch (error) {
					if (
						(!constraints.deviceId && kind !== 'audio') ||
						['NotAllowedError', 'SecurityError'].includes(
							(error as Error).name,
						)
					)
						throw error;
					const { deviceId, noiseCancellationOptions, ...defaults } =
						constraints;
					track = await create(defaults);
				}
				if (!alive.current || generation.current[kind] !== attempt) {
					track.stop();
					throw new Error('Media request cancelled.');
				}
				track.on('started', refresh);
				track.on('stopped', refresh);
				if (kind === 'audio') {
					const audio = track as LocalAudioTrack;
					if (!audioEnabled.current) audio.disable();
					tracks.current.audio = audio;
					setIsKrispInstalled(Boolean(audio.noiseCancellation));
					setIsKrispEnabled(
						Boolean(audio.noiseCancellation?.isEnabled),
					);
				} else {
					tracks.current.video = track as LocalVideoTrack;
					videoWanted.current = true;
				}
				const source =
					(track as LocalAudioTrack).noiseCancellation?.sourceTrack ||
					track.mediaStreamTrack;
				localStorage.setItem(key, source.getSettings().deviceId || '');
				refresh();
				return track;
			})();
			pending.current[kind] = operation.finally(() => {
				delete pending.current[kind];
				if (alive.current)
					setBusy(
						Boolean(pending.current.audio || pending.current.video),
					);
			});
			return pending.current[kind]!;
		},
		[refresh, setIsKrispEnabled, setIsKrispInstalled],
	);
	const getLocalAudioTrack = useCallback(
		(options?: CreateLocalTrackOptions) =>
			acquire('audio', options) as Promise<LocalAudioTrack>,
		[acquire],
	);
	const getLocalVideoTrack = useCallback(
		(options?: CreateLocalTrackOptions) =>
			acquire('video', options) as Promise<LocalVideoTrack>,
		[acquire],
	);
	const initialPending = useRef<Promise<void>>();
	const getAudioAndVideoTracks = useCallback(() => {
		if (initialPending.current) return initialPending.current;
		initialPending.current = (async () => {
			const results = await Promise.allSettled([
				getLocalAudioTrack(),
				...(videoWanted.current ? [getLocalVideoTrack()] : []),
			]);
			const failure = results.find(
				(result) => result.status === 'rejected',
			) as PromiseRejectedResult | undefined;
			if (failure) throw failure.reason;
		})().finally(() => {
			initialPending.current = undefined;
		});
		return initialPending.current;
	}, [getLocalAudioTrack, getLocalVideoTrack]);
	const removeLocalAudioTrack = useCallback(() => remove('audio'), [remove]);
	const removeLocalVideoTrack = useCallback(
		(preserve = false) => remove('video', preserve),
		[remove],
	);
	const isCurrentTrack = useCallback(
		(track: LocalAudioTrack | LocalVideoTrack) =>
			tracks.current[track.kind as Kind] === track,
		[],
	);
	return {
		isCurrentTrack,
		localTracks: Object.values(tracks.current) as (
			LocalAudioTrack | LocalVideoTrack
		)[],
		getLocalAudioTrack,
		getLocalVideoTrack,
		removeLocalAudioTrack,
		removeLocalVideoTrack,
		getAudioAndVideoTracks,
		isAcquiringLocalTracks,
	};
}
